/**
 * The half-written campaign, kept while you go and do something else.
 *
 * Its own module because **two pages need it**: the setup form writes it, and
 * the character sheet reads it to decide where "back" goes. A character rolled
 * from the middle of starting a table should return to the table you were
 * starting, not to the lobby — and the sheet opens in its own tab, so there is
 * no history that could have answered that.
 */

/**
 * Where a half-written campaign is kept while you go and roll a character.
 *
 * **The link to make one leaves the page**, so without this the fix for a
 * missing button would cost the name and premise you had already typed — a
 * worse trade than the button being absent. Session storage rather than local:
 * a draft is for this sitting, and a name you abandoned a week ago reappearing
 * under a new campaign is somebody else's mistake to explain.
 */
const DRAFT_KEY = 'newCampaignDraft';

export interface CampaignDraft {
  readonly name: string;
  readonly premise: string;
}

/**
 * Reads the draft back, and answers empty for anything it cannot trust.
 *
 * **Guarded on `sessionStorage` existing at all**, because a static render has
 * none — the same trap as zustand serving `getInitialState()` to a server
 * render. Unguarded this throws during the first paint and takes the whole
 * page down, which is a much larger bug than the one it exists to fix.
 */
export function readDraft(store?: Pick<Storage, 'getItem'>): CampaignDraft {
  const empty = { name: '', premise: '' };
  const storage = store ?? (typeof sessionStorage === 'undefined' ? undefined : sessionStorage);
  if (!storage) return empty;
  try {
    const raw = storage.getItem(DRAFT_KEY);
    if (!raw) return empty;
    // No shape guard before the destructure: `null` and a bare string both
    // fall to the `catch` or to the two `typeof` checks below, so an object
    // test could not change a single answer. Falsification caught it as a
    // branch that cannot fail, and one mechanism is enough.
    const parsed: unknown = JSON.parse(raw);
    const { name, premise } = parsed as Record<string, unknown>;
    return {
      name: typeof name === 'string' ? name : '',
      premise: typeof premise === 'string' ? premise : '',
    };
  } catch {
    // Malformed or unreadable storage is an empty form, never a crash.
    return empty;
  }
}

/** Keeps the draft, and clears it once the campaign exists. */
export function writeDraft(draft: CampaignDraft | null, store?: Pick<Storage, 'setItem' | 'removeItem'>): void {
  const storage = store ?? (typeof sessionStorage === 'undefined' ? undefined : sessionStorage);
  if (!storage) return;
  try {
    if (draft === null || (draft.name === '' && draft.premise === '')) {
      storage.removeItem(DRAFT_KEY);
      return;
    }
    storage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // A full or blocked store costs the draft and nothing else.
  }
}
